import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Tag,
  Descriptions,
  Tabs,
  Table,
  message,
  Empty,
  Spin,
  DatePicker
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  CalendarOutlined,
  HeartOutlined,
  DollarOutlined,
  BarChartOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { adminAPI, attendanceAPI, measurementsAPI, paymentsAPI } from '../../utils/api';
import moment from 'moment';
import ConsumerEditModal from './ConsumerEditModal';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const ConsumerDetails = () => {
  const [consumer, setConsumer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [payments, setPayments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [consumerModalVisible, setConsumerModalVisible] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const [attendanceDateRange, setAttendanceDateRange] = useState(null);

  useEffect(() => {
    fetchConsumerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      fetchTabData(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, attendanceDateRange]);

  const fetchConsumerDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getConsumer(id);
      setConsumer(response.data.consumer);
    } catch (error) {
      message.error('Failed to fetch consumer details');
      navigate('/consumers');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    try {
      switch (tab) {
        case 'attendance':
          const params = {
            consumer_id: id,
            limit: 50
          };
          if (attendanceDateRange) {
            params.start_date = attendanceDateRange[0].format('YYYY-MM-DD');
            params.end_date = attendanceDateRange[1].format('YYYY-MM-DD');
          }
          const attendanceResponse = await attendanceAPI.getAttendance(params);
          setAttendance(attendanceResponse.data.attendance || []);
          break;
        case 'measurements':
          const measurementsResponse = await measurementsAPI.getMeasurements({
            consumer_id: id,
            limit: 50
          });
          setMeasurements(measurementsResponse.data.measurements || []);
          break;
        case 'payments':
          const paymentsResponse = await paymentsAPI.getPayments({
            consumer_id: id,
            limit: 50
          });
          setPayments(paymentsResponse.data.payments || []);
          break;
        case 'absences':
          setLoadingAbsences(true);
          setLoadingStats(true);
          try {
            const [absencesResponse, statsResponse] = await Promise.all([
              attendanceAPI.getAbsences(id),
              attendanceAPI.getStats(id)
            ]);
            setAbsences(absencesResponse.data.absences || []);
            setStats(statsResponse.data.statistics || null);
          } catch (error) {
            console.error('Error fetching absences/stats:', error);
          } finally {
            setLoadingAbsences(false);
            setLoadingStats(false);
          }
          break;
        default:
          // No action needed for other tabs
          break;
      }
    } catch (error) {
      message.error(`Failed to fetch ${tab} data`);
      if (tab === 'absences') setLoadingAbsences(false);
      if (tab === 'stats') setLoadingStats(false);
    }
  };

  const attendanceColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Check In',
      dataIndex: 'check_in_time',
      key: 'check_in_time',
      render: (time) => time ? moment(time).format('HH:mm A') : '-',
    },
    {
      title: 'Check Out',
      dataIndex: 'check_out_time',
      key: 'check_out_time',
      render: (time) => time ? moment(time).format('HH:mm A') : '-',
    },
    {
      title: 'Duration',
      dataIndex: 'hours_spent',
      key: 'hours_spent',
      render: (hours) => hours ? `${Math.round(hours * 100) / 100}h` : '-',
    },
  ];

  const measurementsColumns = [
    {
      title: 'Date',
      dataIndex: 'measurement_date',
      key: 'measurement_date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight',
      key: 'weight',
      render: (weight) => weight || '-',
    },
    {
      title: 'Height (cm)',
      dataIndex: 'height',
      key: 'height',
      render: (height) => height || '-',
    },
    {
      title: 'BMI',
      dataIndex: 'bmi',
      key: 'bmi',
      render: (bmi) => bmi || '-',
    },
    {
      title: 'Body Fat %',
      dataIndex: 'body_fat_percentage',
      key: 'body_fat_percentage',
      render: (percentage) => percentage ? `${percentage}%` : '-',
    },
  ];

  const paymentsColumns = [
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `₹${amount}`,
    },
    {
      title: 'Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      render: (type) => (
        <Tag color={type === 'monthly' ? 'blue' : 'green'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'completed' ? 'success' :
          status === 'pending' ? 'warning' : 'error';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
  ];

  const absencesColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Day',
      dataIndex: 'day_name',
      key: 'day_name',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!consumer) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty description="Consumer not found" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/consumers')}
            >
              Back
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              Consumer Details
            </Title>
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setConsumerModalVisible(true)}
          >
            Edit Consumer
          </Button>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={
            <Space>
              <UserOutlined />
              Overview
            </Space>
          } key="overview">
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }}>
                    <UserOutlined />
                  </div>
                  <Title level={3} style={{ margin: 0 }}>
                    {consumer.name} <Text type="secondary" style={{ fontSize: '18px', fontWeight: 'normal' }}>{consumer.center_consumer_id}</Text>
                  </Title>
                  <Text type="secondary">{consumer.email}</Text>
                  <div style={{ marginTop: '16px' }}>
                    <Tag
                      color={consumer.is_active ? 'success' : 'default'}
                      style={{ fontSize: '14px', padding: '4px 12px' }}
                    >
                      {consumer.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                  </div>
                </Card>
              </Col>

              <Col xs={24} md={16}>
                <Descriptions
                  title="Personal Information"
                  bordered
                  column={{ xs: 1, sm: 1, md: 2 }}
                  size="small"
                >
                  <Descriptions.Item label="Consumer ID">
                    <Text strong style={{ color: '#1890ff' }}>{consumer.center_consumer_id}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Full Name">
                    {consumer.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {consumer.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {consumer.phone || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gender">
                    {consumer.gender || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Plan Type">
                    <Tag color={consumer.plan_type === 'sample' ? 'purple' : consumer.plan_type === '3_day' ? 'cyan' : 'gold'}>
                      {(consumer.plan_type || 'membership').toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Coach">
                    {consumer.coach_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Added Via">
                    {consumer.added_via ? (
                      <Tag color={consumer.added_via === 'prime_time' ? 'purple' : 'orange'}>
                        {consumer.added_via === 'prime_time' ? 'Prime Time' : 'Physical'}
                      </Tag>
                    ) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">
                    {consumer.date_of_birth ?
                      moment(consumer.date_of_birth).format('MMM DD, YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address">
                    {consumer.address || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Wellness Center">
                    {consumer.center_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Start Date">
                    {moment(consumer.start_date).format('MMM DD, YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Member Since">
                    {moment(consumer.created_at).format('MMM DD, YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={consumer.is_active ? 'success' : 'default'}>
                      {consumer.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Fingerprints">
                    <Space>
                      <Tag color={consumer.fingerprint_template_1 ? 'success' : 'default'}>
                        Finger 1: {consumer.fingerprint_template_1 ? 'Enrolled' : 'Not Enrolled'}
                      </Tag>
                      <Tag color={consumer.fingerprint_template_2 ? 'success' : 'default'}>
                        Finger 2: {consumer.fingerprint_template_2 ? 'Enrolled' : 'Not Enrolled'}
                      </Tag>
                    </Space>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab={
            <Space>
              <CalendarOutlined />
              Attendance
            </Space>
          } key="attendance">
            <Space style={{ marginBottom: 16 }}>
              <Text>Filter by Date:</Text>
              <DatePicker.RangePicker
                onChange={setAttendanceDateRange}
                value={attendanceDateRange}
              />
              {attendanceDateRange && (
                <Button onClick={() => setAttendanceDateRange(null)}>Clear</Button>
              )}
            </Space>
            {attendance.length > 0 ? (
              <Table
                columns={attendanceColumns}
                dataSource={attendance}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                size="small"
              />
            ) : (
              <Empty description="No attendance records found" />
            )}
          </TabPane>

          <TabPane tab={
            <Space>
              <HeartOutlined />
              Health Measurements
            </Space>
          } key="measurements">
            {measurements.length > 0 ? (
              <Table
                columns={measurementsColumns}
                dataSource={measurements}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                size="small"
              />
            ) : (
              <Empty description="No health measurements found" />
            )}
          </TabPane>

          <TabPane tab={
            <Space>
              <DollarOutlined />
              Payments
            </Space>
          } key="payments">
            {payments.length > 0 ? (
              <Table
                columns={paymentsColumns}
                dataSource={payments}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                size="small"
              />
            ) : (
              <Empty description="No payment records found" />
            )}
          </TabPane>

          <TabPane tab={
            <Space>
              <BarChartOutlined />
              Absences & Stats
            </Space>
          } key="absences">
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Space>
                      <BarChartOutlined />
                      Attendance Statistics
                    </Space>
                  }
                  loading={loadingStats}
                >
                  {stats ? (
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Total Attendance">
                        <Text strong>{stats.total_attendance} days</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Current Streak">
                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {stats.current_streak} days 🔥
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Longest Streak">
                        <Text>{stats.longest_streak} days</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="This Month">
                        <Text>{stats.this_month_attendance} days</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Attendance Rate">
                        <Text strong style={{ color: parseFloat(stats.attendance_rate) >= 80 ? '#52c41a' : '#faad14' }}>
                          {stats.attendance_rate}%
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Attendance">
                        {stats.last_attendance_date ?
                          moment(stats.last_attendance_date).format('MMM DD, YYYY') :
                          'Never'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Possible Days">
                        {stats.total_possible_days} days
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Empty description="No statistics available" />
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  title={
                    <Space>
                      <CloseCircleOutlined />
                      Absence Records
                      {absences.length > 0 && (
                        <Tag color="red">{absences.length} absences</Tag>
                      )}
                    </Space>
                  }
                  loading={loadingAbsences}
                >
                  {absences.length > 0 ? (
                    <Table
                      columns={absencesColumns}
                      dataSource={absences}
                      rowKey="date"
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                  ) : (
                    <Empty description="No absence records found. Great attendance!" />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Card>

      <ConsumerEditModal
        visible={consumerModalVisible}
        onCancel={() => setConsumerModalVisible(false)}
        onSuccess={() => {
          setConsumerModalVisible(false);
          fetchConsumerDetails();
        }}
        consumer={consumer}
      />
    </div>
  );
};

export default ConsumerDetails;
